import React, { useCallback, useMemo } from "react";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import {
  ZontalInformationPackageProfile,
  ZontalInformationPackageProfileFilters,
  ZontalInformationPackageProfileFiltersTranslator,
  zontalIPPConstants,
} from "../../../api/Zontal";
import { useInfiniteListZontalIPP } from "../../useZontalAPI";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import { ZontalInformationPackageProfileFilterForm, ZontalIPPFilterBar } from "./ZontalIPPFilterBar";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { useDownloadZontalDatasets, ZontalIcon } from "../../ZontalCommons";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { ResourceName } from "../../../main/Routing";
import { Button } from "../../../common/buttons/Button/Button";

export const switchZontalIPPDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<ZontalInformationPackageProfileFilters>>[] | null | undefined
): SortState<InferSortByOptions<ZontalInformationPackageProfileFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return [{ headerId: "default-name", sortDirection: "DESC", property: "NAME" }];
};

export const defaultZontalIPPFilters: ZontalInformationPackageProfileFilters = {
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
};

export const useZontalIPPTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"zontalIPP">) => {
  const defaults: ColumnsSettings<ZontalInformationPackageProfile> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-enteredOn": { pos: 3, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 4, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 5, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-lastModifiedBy": {
        pos: 6,
        active: true,
        header: fieldLabels.lastModifiedBy,
        property: "lastModifiedBy",
      },
      "default-uid": { pos: 7, active: true, header: fieldLabels.uid || "UID", pcroperty: "uid" },
      "default-owners": { pos: 8, active: true, header: fieldLabels.owners || "Owners", property: "owners" },
      "default-version": { pos: 9, active: true, header: fieldLabels.version || "Version", property: "version" },
      "default-adfIdentifier": {
        pos: 10,
        active: true,
        header: fieldLabels.adfIdentifier || "ADF Identifier",
        property: "adfIdentifier",
      },
      "default-adfVersion": {
        pos: 11,
        active: true,
        header: fieldLabels.adfVersion || "ADF Version",
        property: "adfVersion",
      },
      "default-digitalObjectsContentLength": {
        pos: 12,
        active: true,
        header: fieldLabels.digitalObjectsContentLength || "Digital Objects Content Length",
        property: "digitalObjectsContentLength",
      },
      "default-digitalObjectsCount": {
        pos: 13,
        active: true,
        header: fieldLabels.digitalObjectsCount || "Digital Objects Count",
        property: "digitalObjectsCount",
      },
      "default-contentIdentifier": {
        pos: 14,
        active: true,
        header: fieldLabels.contentIdentifier || "Content Identifier",
        property: "contentIdentifier",
      },
      "default-informationPackageProfile": {
        pos: 2,
        active: true,
        header: fieldLabels.informationPackageProfile || "Information Package Profile",
        property: "informationPackageProfile",
      },
      "default-lifecycleStatus": {
        pos: 15,
        active: true,
        header: fieldLabels.lifecycleStatus || "Lifecycle Status",
        property: "lifecycleStatus",
      },
      "default-transitionStatus": {
        pos: 16,
        active: true,
        header: fieldLabels.transitionStatus || "Transition Status",
        property: "transitionStatus",
      },
      "default-datahubLoaded": {
        pos: 17,
        active: true,
        header: fieldLabels.datahubLoaded || "Datahub Loaded",
        property: "datahubLoaded",
      },
      "default-submittedOn": {
        pos: 18,
        active: true,
        header: fieldLabels.submittedOn || "Submitted On",
        property: "submittedOn",
      },
      "default-submittedBy": {
        pos: 19,
        active: true,
        header: fieldLabels.submittedBy || "Submitted By",
        property: "submittedBy",
      },
      "default-motivatedBy": {
        pos: 20,
        active: true,
        header: fieldLabels.motivatedBy || "Motivated By",
        property: "motivatedBy",
      },
      "default-expired": { pos: 21, active: true, header: fieldLabels.expired || "Expired", property: "expired" },
      "default-digestValue": {
        pos: 22,
        active: true,
        header: fieldLabels.digestValue || "Digest Value",
        property: "digestValue",
      },
    }),
    [fieldLabels]
  );
  const tabStoreDefaults: TableTabsDict<
    ZontalInformationPackageProfile,
    ZontalInformationPackageProfileFilters,
    ZontalInformationPackageProfileFilterForm
  > = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultZontalIPPFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );
  return { defaults, tabStoreDefaults };
};

export const useZontalIPPTableColumns: UseEntityTableColumns<"zontalIPP"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"zontalIPP">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<ZontalInformationPackageProfile> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "left",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div className="flex gap-5" style={{ height: "max-content" }} data-toggle="tooltip" title={row.name}>
            <div className="flex align-center gap-5">
              <LucideIcon name={entityConstants.icon} color="var(--primary)" />
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  marginRight: "5px",
                }}
              >
                {row.name}
              </div>
            </div>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => row.enteredBy.id,
        width: 200,
        align: "left",
        // ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-lastModifiedBy",
        Header: fieldLabels.lastModifiedBy,
        accessor: (row) => row.lastModifiedBy.id,
        width: 200,
        align: "left",
        // ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
      {
        id: "default-uid",
        Header: fieldLabels.uid || "UID",
        accessor: (row) => row.uid,
        width: 200,
        align: "left",
      },
      {
        id: "default-owners",
        Header: fieldLabels.owners || "Owners",
        accessor: (row) => (Array.isArray(row.owners) ? row.owners.map((o) => o?.name || o?.id || "").join(", ") : ""),
        width: 200,
        align: "left",
      },
      {
        id: "default-version",
        Header: fieldLabels.version || "Version",
        accessor: (row) => row.version,
        width: 120,
        align: "right",
      },
      {
        id: "default-adfIdentifier",
        Header: fieldLabels.adfIdentifier || "ADF Identifier",
        accessor: (row) => row.adfIdentifier,
        width: 250,
        align: "left",
      },
      {
        id: "default-adfVersion",
        Header: fieldLabels.adfVersion || "ADF Version",
        accessor: (row) => (Array.isArray(row.adfVersion) ? row.adfVersion.join(", ") : row.adfVersion),
        width: 150,
        align: "left",
      },
      {
        id: "default-digitalObjectsContentLength",
        Header: fieldLabels.digitalObjectsContentLength || "Digital Objects Content Length",
        accessor: (row) => row.digitalObjectsContentLength,
        width: 180,
        align: "right",
      },
      {
        id: "default-digitalObjectsCount",
        Header: fieldLabels.digitalObjectsCount || "Digital Objects Count",
        accessor: (row) => row.digitalObjectsCount,
        width: 150,
        align: "right",
      },
      {
        id: "default-contentIdentifier",
        Header: fieldLabels.contentIdentifier || "Content Identifier",
        accessor: (row) => row.contentIdentifier,
        width: 200,
        align: "left",
      },
      {
        id: "default-informationPackageProfile",
        Header: fieldLabels.informationPackageProfile || "Information Package Profile",
        accessor: (row) => (
          <label className="label label-soft-success">
            {row.informationPackageProfile?.name || row.informationPackageProfile?.id || ""}
          </label>
        ),
        width: 200,
        align: "left",
      },
      {
        id: "default-lifecycleStatus",
        Header: fieldLabels.lifecycleStatus || "Lifecycle Status",
        accessor: (row) => row.lifecycleStatus?.name || row.lifecycleStatus?.id || "",
        width: 180,
        align: "left",
      },
      {
        id: "default-transitionStatus",
        Header: fieldLabels.transitionStatus || "Transition Status",
        accessor: (row) => row.transitionStatus?.name || row.transitionStatus?.id || "",
        width: 180,
        align: "left",
      },
      {
        id: "default-datahubLoaded",
        Header: fieldLabels.datahubLoaded || "Datahub Loaded",
        accessor: (row) => (row.datahubLoaded ? "Yes" : "No"),
        width: 120,
        align: "center",
      },
      {
        id: "default-submittedOn",
        Header: fieldLabels.submittedOn || "Submitted On",
        accessor: (row) => <DateTimeRenderer date={row.submittedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
      },
      {
        id: "default-submittedBy",
        Header: fieldLabels.submittedBy || "Submitted By",
        accessor: (row) => row.submittedBy?.name || row.submittedBy?.id || "",
        width: 200,
        align: "left",
      },
      {
        id: "default-motivatedBy",
        Header: fieldLabels.motivatedBy || "Motivated By",
        accessor: (row) => row.motivatedBy,
        width: 200,
        align: "left",
      },
      {
        id: "default-expired",
        Header: fieldLabels.expired || "Expired",
        accessor: (row) => (row.expired ? "Yes" : "No"),
        width: 100,
        align: "center",
      },
      {
        id: "default-digestValue",
        Header: fieldLabels.digestValue || "Digest Value",
        accessor: (row) => row.digestValue,
        width: 250,
        align: "left",
      },
    ];

    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.adfIdentifier,
    fieldLabels.adfVersion,
    fieldLabels.contentIdentifier,
    fieldLabels.datahubLoaded,
    fieldLabels.digestValue,
    fieldLabels.digitalObjectsContentLength,
    fieldLabels.digitalObjectsCount,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.expired,
    fieldLabels.id,
    fieldLabels.informationPackageProfile,
    fieldLabels.lastModifiedBy,
    fieldLabels.lifecycleStatus,
    fieldLabels.modifiedOn,
    fieldLabels.motivatedBy,
    fieldLabels.name,
    fieldLabels.owners,
    fieldLabels.submittedBy,
    fieldLabels.submittedOn,
    fieldLabels.transitionStatus,
    fieldLabels.uid,
    fieldLabels.version,
    setSort,
    sort,
  ]);
  return { columns };
};

export const ZontalIPPTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"zontalIPP">) => {
  const { defaults, tabStoreDefaults } = useZontalIPPTableDefaults({ fieldLabels });
  const { selection, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<ZontalInformationPackageProfile>();
  const {
    filters,
    forcedFilters,
    sidebarFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<
    ZontalInformationPackageProfile,
    ZontalInformationPackageProfileFilters,
    ZontalInformationPackageProfileFilterForm
  >({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
    keyOverride: "ZontalIPPTable",
  });
  const { onTabChange, sort, setSort, searchValue, setSearchValue, functionRef } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchZontalIPPDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useZontalIPPTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  const infiniteQuery = useInfiniteListZontalIPP(
    `${zontalIPPConstants.resource}/list` as ResourceName,
    memoizedFilters,
    { enabled: true }
  );

  const onFinishCallback = useCallback(() => {
    functionRef.current?.resetSelection();
  }, [functionRef]);

  const { download, isLoading } = useDownloadZontalDatasets(onFinishCallback);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel
            entityConstants={entityConstants}
            disableCount
            iconOverride={<ZontalIcon style={{ width: 24, height: 24 }} />}
          />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <></>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <ZontalIPPFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
            //   style={{
            //     borderTop: "0px",
            //     borderRadius: "0px",
            //   }}
            >
              <EntityFilterIndicator<ZontalInformationPackageProfile, ZontalInformationPackageProfileFilters>
                filters={filters}
                excludeFilters={{
                  sortBy: (value) => false,
                }}
                translatorConsts={ZontalInformationPackageProfileFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
              <Button
                className={"btn btn-primary"}
                onClick={async () => await download(Array.from(selection))}
                disabled={!selection.size}
                loading={isLoading}
                title="Download"
              >
                <LucideIcon name="download" /> Download
              </Button>
            </EntityTable.Controls>
            <EntityTable.Body<ZontalInformationPackageProfile, ZontalInformationPackageProfileFilters>
              // functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn={false}
              loading={tabsLoading}
              infiniteQueryLoaderOverride={infiniteQuery}
              // disableCheckboxes
              disablePermissionQuery
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
